import { useUnmappedCadMaterials } from "@api/unmappedCadMaterials";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import { alpha, Box, darken, lighten, Theme, useTheme } from "@mui/material";
import { GridColDef, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

const UnMappedCadMaterialGrid = observer((props: any) => {
  const theme = useTheme();
  const [rows, setRows] = useState<any[]>([]);
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const unmappedCadMaterials = useUnmappedCadMaterials(props.profileId || null);

  useEffect(() => {
    const newRows =
      unmappedCadMaterials.data?.map((material, index) => ({
        ...material,
        id: index,
      })) || [];
    setRows(newRows as any);
  }, [unmappedCadMaterials.data]);

  const columns: GridColDef[] = [
    {
      field: "materialName",
      headerName: i18n.t("unmapped-cad-material-tab.grid.columns.material"),
      editable: false,
      flex: 1,
      type: "string",
    },
    {
      field: "profileName",
      headerName: i18n.t("unmapped-cad-material-tab.grid.columns.profile"),
      editable: false,
      flex: 1,
      type: "string",
    },
  ];

  const ExcelExportToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: 1,
          borderBottom: 1,
          backgroundColor: getBackgroundColor(theme),
          borderColor: getBorderColor(theme),
          fontWeight: "bold",
        }}
      >
        <GridToolbarExport csvOptions={{ disableToolbarButton: true }} printOptions={{ disableToolbarButton: true }} />
      </GridToolbarContainer>
    );
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
      <DataGrid
        loading={unmappedCadMaterials.isFetching || unmappedCadMaterials.isPending}
        columns={columns}
        rows={rows}
        disableColumnMenu
        density="compact"
        hideFooter={false}
        slots={{
          toolbar: ExcelExportToolbar,
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "materialName", sort: "asc" }],
          },
        }}
        sx={{ height: `${(height || 400) - 16}px` }}
      />
    </Box>
  );
});

const getBorderColor = (theme: any) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.background.default, 1), 0.88);
  }
  return darken(alpha(theme.palette.background.default, 1), 0.68);
};

const getBackgroundColor = (theme: Theme) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.divider, 1), 0.92);
  }
  return darken(alpha(theme.palette.divider, 1), 0.82);
};

export default UnMappedCadMaterialGrid;
